import { useMemo } from 'react'
import { AlertTriangle, ReverCard, toast } from '@itsrever/design-system'
import { ReturnReason } from './reason'
import { ReasonsHeader } from './reasons-header'
import { useTranslation } from 'react-i18next'
import { DashboardReason } from '@/domain/return-reasons'

interface ReasonsCardProps {
  reasons: DashboardReason[]
  handleChangeCategoryIds: (categoryIds: string[], reasonIndex: number) => void
  handleChangeMandatoryComment: (value: boolean, reasonIndex: number) => void
  readOnly: boolean
  isShopify: boolean
}

export const ReasonsWithoutChildren: React.FC<ReasonsCardProps> = ({
  reasons,
  handleChangeCategoryIds,
  handleChangeMandatoryComment,
  readOnly,
  isShopify
}) => {
  const { t } = useTranslation()
  const orderedReasons = useFrozenOrder(reasons)

  const changeValues = (values: string[], index?: number) => {
    if (typeof index !== 'number') return
    const reason = reasons.find((reason) => reason.index === index)
    if (reason?.parentReason.reason === 'NOT_RECEIVED' && values?.[0] === '*') {
      const reasonName = t('select_reason.NOT_RECEIVED') + ''
      const toastText = t('edit_reasons.instant_refund_warning_title', {
        reason: reasonName
      })
      toast({
        variant: 'warning',
        text: '',
        component: (
          <div className="flex items-center gap-1">
            <AlertTriangle className="size-8" />
            <p
              dangerouslySetInnerHTML={{
                __html: toastText
              }}
            />
          </div>
        )
      })
    }
    handleChangeCategoryIds(values, index)
  }

  return (
    <ReverCard className="mb-4 p-4" fullWidth>
      <div className="mb-3 border-b border-solid border-gray-300 pb-2">
        <p>
          <b>{t('drawer_pages.reasons')}</b>
        </p>
      </div>

      <ReasonsHeader isShopify={isShopify} />
      <ul>
        {orderedReasons.map((reason) => {
          return (
            <ReturnReason
              key={reason.parentReason.reason}
              index={reason.index}
              reason={reason.parentReason}
              changeValues={changeValues}
              changeMandatoryComment={(value, ind) => {
                if (typeof ind === 'number')
                  handleChangeMandatoryComment(value, ind)
              }}
              readOnly={readOnly}
              isShopify={isShopify}
            />
          )
        })}
      </ul>
    </ReverCard>
  )
}

// Enabled reasons come first, but the order is frozen for as long as the same
// reasons are loaded: re-sorting on each toggle moves rows out from under the
// pointer and carries half-typed product types to another reason.
function useFrozenOrder(reasons: DashboardReason[]) {
  const names = reasons.map((reason) => reason.parentReason.reason)
  const order = useMemo(
    () => orderReasonsByEnabled(reasons).map((r) => r.parentReason.reason),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [names.join('|')]
  )

  return order
    .map((name) => reasons.find((r) => r.parentReason.reason === name))
    .filter((reason): reason is DashboardReason => reason !== undefined)
}

const orderReasonsByEnabled = (reasons: DashboardReason[]) => {
  return [...reasons].sort(
    (a, b) =>
      Number(b.parentReason.categoryIds.length > 0) -
      Number(a.parentReason.categoryIds.length > 0)
  )
}
